<script setup lang="ts">
import { clinicSeoTitle, clinicTitleSuffix, clinicMetaDescription, clinicSchemaDescription, convertToOpeningHours } from '~/composables/useSeoHelpers'

const route = useRoute()

const countySlug = computed(() => route.params.county as string)
const citySlug = computed(() => route.params.city as string)
const clinicSlug = computed(() => route.params.clinic as string)

const { data: clinicData, pending } = await useClinic(clinicSlug)

const { isClinicOwner } = useUser()

// Track analytics
const { trackPageView, trackPhoneClick, trackDirectionsClick } = useClinicAnalytics()
const { capture } = useAnalytics()
// Contextual review prompt (appears after 20s)
const reviewOpenTrigger = ref(0)
const showReviewPrompt = ref(false)
let reviewPromptTimer: ReturnType<typeof setTimeout> | undefined

function dismissReviewPrompt() {
  showReviewPrompt.value = false
  if (reviewPromptTimer) clearTimeout(reviewPromptTimer)
}

function scrollToReviewsAndOpen() {
  dismissReviewPrompt()
  document.getElementById('recenze')?.scrollIntoView({ behavior: 'smooth' })
  reviewOpenTrigger.value++
}

onMounted(() => {
  if (clinicData.value?.data?.id) {
    trackPageView(clinicData.value.data.id)
  }
  reviewPromptTimer = setTimeout(() => {
    showReviewPrompt.value = true
  }, 15000)
})

onUnmounted(() => {
  if (reviewPromptTimer) clearTimeout(reviewPromptTimer)
})

const onMobilePhoneClick = () => {
  if (clinicData.value?.data?.id) {
    trackPhoneClick(clinicData.value.data.id)
    const c = clinicData.value.data
    capture('clinic_phone_clicked', { clinic_id: c.id, clinic_name: c.name, is_premium: c.isPremium, is_emergency_phone: isMobileShowingEmergencyPhone.value })
  }
}
const onMobileDirectionsClick = () => {
  if (clinicData.value?.data?.id) {
    trackDirectionsClick(clinicData.value.data.id)
    const c = clinicData.value.data
    capture('clinic_directions_clicked', { clinic_id: c.id, clinic_name: c.name, is_premium: c.isPremium })
  }
}

const clinic = computed(() => clinicData.value?.data)

// Emergency-aware phone routing for mobile sticky bar
const { isEmergencyActive } = useEmergencyStatus(
  computed(() => clinic.value?.workingHours ?? null),
  computed(() => clinic.value?.emergencyHours ?? null),
  computed(() => clinic.value?.is24Hours ?? false),
)

const mobileActivePhone = computed(() => {
  if (!clinic.value) return null
  if (isEmergencyActive.value && clinic.value.emergencyPhone) {
    return clinic.value.emergencyPhone
  }
  return clinic.value.phone || clinic.value.emergencyPhone || null
})

const isMobileShowingEmergencyPhone = computed(() => {
  if (!clinic.value) return false
  if (isEmergencyActive.value && clinic.value.emergencyPhone) return true
  if (!clinic.value.phone && clinic.value.emergencyPhone) return true
  return false
})

const directionsUrl = computed(() => {
  if (!clinic.value?.latitude || !clinic.value?.longitude) return null
  return `https://www.google.com/maps/dir/?api=1&destination=${clinic.value.latitude},${clinic.value.longitude}`
})

if (!pending.value && !clinic.value) {
  throw createError({
    statusCode: 404,
    message: 'Klinika nenalezena',
  })
}

// Fetch similar clinics in the same city
const { data: similarClinics } = await useClinics({
  citySlug: citySlug.value,
  limit: 4,
})

const filteredSimilarClinics = computed(() => {
  if (!similarClinics.value?.data || !clinic.value) return []
  return similarClinics.value.data.filter((c) => c.id !== clinic.value?.id).slice(0, 3)
})

useSeoMeta({
  title: () =>
    clinic.value
      ? clinicSeoTitle(clinic.value.name, clinic.value.city.name)
      : 'Veterinární klinika',
  titleTemplate: () =>
    clinic.value
      ? `%s – ${clinicTitleSuffix(clinic.value.isEmergency)}`
      : '%s – Veterina Pohotovost',
  description: () =>
    clinic.value
      ? clinicMetaDescription(
          clinic.value.name,
          clinic.value.city.name,
          clinic.value.isEmergency,
          clinic.value.is24Hours,
          clinic.value.phone,
          clinic.value.about,
        )
      : '',
  twitterCard: 'summary_large_image',
})

defineOgImage({
  props: {
    title: computed(() => clinic.value?.name || ''),
    description: computed(() =>
      clinic.value
        ? `${clinicTitleSuffix(clinic.value.isEmergency)} v ${clinic.value.city.name}`
        : '',
    ),
  },
})

// Schema markup for SEO
const r2PublicUrl = useRuntimeConfig().public.r2PublicUrl

useHead(() => {
  if (!clinic.value) return {}

  const c = clinic.value
  let schemaImage: string | string[] | undefined
  if (c.isPremium && c.images.length > 0) {
    schemaImage = c.images.map((key: string) => `${r2PublicUrl}/${key}`)
  } else {
    schemaImage = c.streetViewUrl || undefined
  }

  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'VeterinaryCare',
    name: c.name,
    description: clinicSchemaDescription(c.about, c.name, c.city.name),
    image: schemaImage,
    address: {
      '@type': 'PostalAddress',
      streetAddress: c.street || undefined,
      addressLocality: c.city.name,
      addressRegion: c.city.county.name,
      postalCode: c.postalCode || undefined,
      addressCountry: 'CZ',
    },
    geo: c.latitude && c.longitude
      ? {
          '@type': 'GeoCoordinates',
          latitude: c.latitude,
          longitude: c.longitude,
        }
      : undefined,
    telephone: c.phone || undefined,
    email: c.email || undefined,
    url: c.website || undefined,
    sameAs: c.website ? [c.website] : undefined,
    openingHoursSpecification: convertToOpeningHours(c.workingHours),
    aggregateRating: c.rating
      ? {
          '@type': 'AggregateRating',
          ratingValue: c.rating,
          reviewCount: c.reviewCount,
        }
      : undefined,
  }

  if (c.isPremium && c.specializations.length > 0) {
    schema.medicalSpecialty = c.specializations
  }

  if (c.isPremium && c.services.length > 0) {
    schema.makesOffer = c.services.map((service: string) => ({
      '@type': 'Offer',
      itemOffered: {
        '@type': 'Service',
        name: service,
      },
    }))
  }

  if (c.isPremium && c.teamMembers && c.teamMembers.length > 0) {
    schema.employee = c.teamMembers.map((member) => ({
      '@type': 'Person',
      name: member.name,
      jobTitle: member.role || undefined,
      image: member.image ? `${r2PublicUrl}/${member.image}` : undefined,
    }))
  }

  return {
    script: [
      {
        type: 'application/ld+json',
        innerHTML: JSON.stringify(schema),
      },
    ],
  }
})

// Breadcrumb schema via useSchemaOrg (auto-resolves relative URLs)
useSchemaOrg([
  defineBreadcrumb({
    itemListElement: [
      { name: 'Domů', item: '/' },
      {
        name: computed(() => clinic.value?.city.county.name || ''),
        item: computed(() => `/${countySlug.value}`),
      },
      {
        name: computed(() => clinic.value?.city.name || ''),
        item: computed(() => `/${countySlug.value}/${citySlug.value}`),
      },
      { name: computed(() => clinic.value?.name || '') },
    ],
  }),
])

const breadcrumbs = computed(() => [
  { label: 'Domů', to: '/' },
  { label: clinic.value?.city.county.name || '...', to: `/${countySlug.value}` },
  { label: clinic.value?.city.name || '...', to: `/${countySlug.value}/${citySlug.value}` },
  { label: clinic.value?.name || '...' },
])
</script>

<template>
  <div class="pb-24 md:pb-8">
    <div class="container mx-auto px-4 py-8">
      <UBreadcrumb :items="breadcrumbs" class="mb-6" />

      <div v-if="pending" class="space-y-6">
        <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <USkeleton class="h-96 lg:col-span-2" />
          <div class="space-y-6">
            <USkeleton class="h-64" />
            <USkeleton class="h-48" />
          </div>
        </div>
      </div>

      <div v-else-if="clinic" class="space-y-8">
        <!-- Clinic Detail Card -->
        <ClinicDetail :clinic="clinic" />

        <!-- Reviews Section -->
        <UCard id="recenze">
          <ReviewList
            :clinic-slug="clinicSlug"
            :clinic-id="clinic.id"
            :clinic-name="clinic.name"
            :trigger-open="reviewOpenTrigger"
          />
        </UCard>

        <!-- Similar Clinics Section -->
        <section v-if="filteredSimilarClinics.length > 0">
          <div class="flex items-center justify-between mb-4">
            <h2 class="text-lg font-semibold text-highlighted">
              Další kliniky v {{ clinic.city.name }}
            </h2>
            <NuxtLink
              :to="`/${countySlug}/${citySlug}`"
              class="text-sm text-primary hover:underline flex items-center gap-1"
            >
              Zobrazit vše
              <UIcon name="i-lucide-arrow-right" class="w-4 h-4" />
            </NuxtLink>
          </div>
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <ClinicCard
              v-for="similarClinic in filteredSimilarClinics"
              :key="similarClinic.id"
              :clinic="similarClinic"
            />
          </div>
        </section>
      </div>
    </div>

    <!-- Floating review nudge (appears after 20s) -->
    <ClientOnly>
      <Transition name="slide-up">
        <button
          v-if="showReviewPrompt && !isClinicOwner"
          class="fixed right-4 bottom-20 md:bottom-6 z-50 flex items-center gap-2 rounded-full bg-primary text-white pl-3.5 pr-2 py-2.5 shadow-lg hover:shadow-xl transition-shadow cursor-pointer"
          @click="scrollToReviewsAndOpen"
        >
          <UIcon name="i-lucide-star" class="size-4" />
          <span class="text-base font-medium">Ohodnotit kliniku</span>
          <span
            class="rounded-full hover:bg-white/20 p-0.5 transition-colors"
            role="button"
            aria-label="Zavřít"
            @click.stop="dismissReviewPrompt"
          >
            <UIcon name="i-lucide-x" class="size-3.5" />
          </span>
        </button>
      </Transition>
    </ClientOnly>

    <!-- Mobile Sticky Actions -->
    <div
      v-if="clinic && (mobileActivePhone || directionsUrl)"
      class="fixed bottom-0 left-0 right-0 p-4 bg-default/95 backdrop-blur-sm border-t border-default md:hidden z-40 safe-area-inset-bottom"
    >
      <div class="flex gap-3">
        <ClientOnly>
          <UButton
            v-if="mobileActivePhone"
            :to="`tel:${mobileActivePhone.replace(/\s/g, '')}`"
            :color="isMobileShowingEmergencyPhone ? 'warning' : 'primary'"
            size="lg"
            class="flex-1"
            :icon="isMobileShowingEmergencyPhone ? 'i-lucide-siren' : 'i-lucide-phone'"
            @click="onMobilePhoneClick"
          >
            {{ isMobileShowingEmergencyPhone ? 'Zavolat (pohotovost)' : 'Zavolat' }}
          </UButton>
          <template #fallback>
            <UButton
              v-if="clinic.phone || clinic.emergencyPhone"
              :to="`tel:${(clinic.phone || clinic.emergencyPhone || '').replace(/\s/g, '')}`"
              :color="!clinic.phone ? 'warning' : 'primary'"
              size="lg"
              class="flex-1"
              :icon="!clinic.phone ? 'i-lucide-siren' : 'i-lucide-phone'"
              @click="onMobilePhoneClick"
            >
              {{ !clinic.phone ? 'Zavolat (pohotovost)' : 'Zavolat' }}
            </UButton>
          </template>
        </ClientOnly>
        <UButton
          v-if="directionsUrl"
          :to="directionsUrl"
          target="_blank"
          color="neutral"
          variant="outline"
          size="lg"
          class="flex-1"
          icon="i-lucide-navigation"
          @click="onMobileDirectionsClick"
        >
          Navigovat
        </UButton>
      </div>
    </div>
  </div>
</template>

<style scoped>
.slide-up-enter-active {
  transition: all 0.4s ease-out;
}
.slide-up-leave-active {
  transition: all 0.2s ease-in;
}
.slide-up-enter-from {
  opacity: 0;
  transform: translateY(1rem);
}
.slide-up-leave-to {
  opacity: 0;
  transform: translateY(0.5rem);
}
</style>
